import axios from "axios";

const API_URL = process.env.REACT_APP_BASEURL;

const jsonHeaders = {
  "Content-Type": "application/json",
  Accept: "application/json",
};

const multipartHeaders = {
  "Content-Type": "multipart/form-data",
  Accept: "application/json",
};

// Packages/Quick Trips errors are always { error: "message" }, or on a
// validation failure { error: "Validation Error", details: [...] } — unlike
// the { errors: [...] } shape other services' extractErrorMessage handles.
const extractErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data) return error.message || "Something went wrong";
  if (Array.isArray(data.details)) return data.details.join(", ");
  if (Array.isArray(data.errors)) return data.errors.join(", ");
  return data.error || data.message || "Something went wrong";
};

const request = async (url, options = {}) => {
  try {
    const response = await axios(url, { withCredentials: true, ...options });
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error(extractErrorMessage(error));
  }
};

// ─────────────────────────────────────────────
// ADMIN — Quick Trips
// ─────────────────────────────────────────────

// params: { page, pageSize }
export const getAllQuickTrips = (params = {}) =>
  request(`${API_URL}/admin/quick-trips`, {
    method: "GET",
    headers: jsonHeaders,
    params,
  });

export const getQuickTripById = (id) =>
  request(`${API_URL}/admin/quick-trips/${id}`, {
    method: "GET",
    headers: jsonHeaders,
  });

export const createQuickTrip = (data) =>
  request(`${API_URL}/admin/quick-trips`, {
    method: "POST",
    headers: jsonHeaders,
    data,
  });

// data may be a FormData (image upload — appended to image_urls, not a
// replace) or a plain object (JSON-only partial update).
export const updateQuickTrip = ({ id, data }) =>
  request(`${API_URL}/admin/quick-trips/${id}`, {
    method: "PUT",
    headers: data instanceof FormData ? multipartHeaders : jsonHeaders,
    data,
  });

// files: File[] — up to 10 accepted server-side (this app caps the UI at 5).
// Appended to the quick trip's existing image_urls, never a replace.
export const uploadQuickTripImages = ({ id, files }) => {
  const formData = new FormData();
  files.forEach((file) => formData.append("images", file));
  return updateQuickTrip({ id, data: formData });
};

export const deleteQuickTrip = (id) =>
  request(`${API_URL}/admin/quick-trips/${id}`, {
    method: "DELETE",
    headers: jsonHeaders,
  });

export const restoreQuickTrip = (id) =>
  request(`${API_URL}/admin/quick-trips/${id}/restore`, {
    method: "PUT",
    headers: jsonHeaders,
  });

// ─────────────────────────────────────────────
// ADMIN — Packages
// ─────────────────────────────────────────────

// params: { page, pageSize }
export const getAllPackages = (params = {}) =>
  request(`${API_URL}/admin/packages`, {
    method: "GET",
    headers: jsonHeaders,
    params,
  });

export const getPackageById = (id) =>
  request(`${API_URL}/admin/packages/${id}`, {
    method: "GET",
    headers: jsonHeaders,
  });

export const createPackage = (data) =>
  request(`${API_URL}/admin/packages`, {
    method: "POST",
    headers: jsonHeaders,
    data,
  });

// data may be a FormData (image upload — appended to image_urls, not a
// replace) or a plain object (JSON-only partial update).
export const updatePackage = ({ id, data }) =>
  request(`${API_URL}/admin/packages/${id}`, {
    method: "PUT",
    headers: data instanceof FormData ? multipartHeaders : jsonHeaders,
    data,
  });

// files: File[] — up to 10 accepted server-side (this app caps the UI at 5).
// Appended to the package's existing image_urls, never a replace.
export const uploadPackageImages = ({ id, files }) => {
  const formData = new FormData();
  files.forEach((file) => formData.append("images", file));
  return updatePackage({ id, data: formData });
};

export const deletePackage = (id) =>
  request(`${API_URL}/admin/packages/${id}`, {
    method: "DELETE",
    headers: jsonHeaders,
  });

export const restorePackage = (id) =>
  request(`${API_URL}/admin/packages/${id}/restore`, {
    method: "PUT",
    headers: jsonHeaders,
  });

// ─────────────────────────────────────────────
// ADMIN — Customizations (catalog + per-package links)
// ─────────────────────────────────────────────

// params: { category } — HOTEL | CAR | ROOM | FOOD | TOUR | OTHERS
export const getCustomizationOptions = (params = {}) =>
  request(`${API_URL}/admin/customizations`, {
    method: "GET",
    headers: jsonHeaders,
    params,
  });

// data: a plain { category, name, description?, media_url?, data? } object,
// or a FormData carrying those same fields plus an "image" file (sets
// media_url outright — the catalog schema is flat enough for multipart to
// carry the whole record in one request, unlike Packages/QuickTrips).
export const createCustomizationOption = (data) =>
  request(`${API_URL}/admin/customizations`, {
    method: "POST",
    headers: data instanceof FormData ? multipartHeaders : jsonHeaders,
    data,
  });

export const updateCustomizationOption = ({ id, data }) =>
  request(`${API_URL}/admin/customizations/${id}`, {
    method: "PUT",
    headers: data instanceof FormData ? multipartHeaders : jsonHeaders,
    data,
  });

// Cascades on the backend — removes this option from every package that
// currently links to it, not just one.
export const deleteCustomizationOption = (id) =>
  request(`${API_URL}/admin/customizations/${id}`, {
    method: "DELETE",
    headers: jsonHeaders,
  });

export const getPackageCustomizations = (packageId) =>
  request(`${API_URL}/admin/packages/${packageId}/customizations`, {
    method: "GET",
    headers: jsonHeaders,
  });

// payload: { packageId, data: { customization_option_id, price_delta, is_default } }
export const addPackageCustomization = ({ packageId, data }) =>
  request(`${API_URL}/admin/packages/${packageId}/customizations`, {
    method: "POST",
    headers: jsonHeaders,
    data,
  });

// payload: { packageId, optionId } — optionId is the CustomizationOption id,
// not the PackageCustomizationOption link row's own id.
export const removePackageCustomization = ({ packageId, optionId }) =>
  request(
    `${API_URL}/admin/packages/${packageId}/customizations/${optionId}`,
    {
      method: "DELETE",
      headers: jsonHeaders,
    },
  );

// ─────────────────────────────────────────────
// ADMIN — Saved data (read-only, customer booking drafts)
// ─────────────────────────────────────────────

// params: { page, pageSize }
export const getAllSavedData = (params = {}) =>
  request(`${API_URL}/admin/saved-data`, {
    method: "GET",
    headers: jsonHeaders,
    params,
  });

// ─────────────────────────────────────────────
// ADMIN — Bookings (read-only, finalized checkouts)
// ─────────────────────────────────────────────

// params: { page, pageSize }
export const getAllPackageBookings = (params = {}) =>
  request(`${API_URL}/admin/packages/bookings`, {
    method: "GET",
    headers: jsonHeaders,
    params,
  });

export const getPackageBookingById = (id) =>
  request(`${API_URL}/admin/packages/bookings/${id}`, {
    method: "GET",
    headers: jsonHeaders,
  });

// ─────────────────────────────────────────────
// ADMIN — Custom package requests ("Request A Custom Package" lead capture)
// ─────────────────────────────────────────────

// params: { page, pageSize }
export const getAllCustomPackageRequests = (params = {}) =>
  request(`${API_URL}/admin/packages/custom-package-requests`, {
    method: "GET",
    headers: jsonHeaders,
    params,
  });

export const getCustomPackageRequestById = (id) =>
  request(`${API_URL}/admin/packages/custom-package-requests/${id}`, {
    method: "GET",
    headers: jsonHeaders,
  });

// payload: { id, status } — status: REQUESTED | APPROVED | DENIED. This is
// the only admin-editable field on the record; nothing else is writable.
export const updateCustomPackageRequestStatus = ({ id, status }) =>
  request(`${API_URL}/admin/packages/custom-package-requests/${id}/status`, {
    method: "PUT",
    headers: jsonHeaders,
    data: { status },
  });

// Hard delete — no soft-delete/restore for this resource.
export const deleteCustomPackageRequest = (id) =>
  request(`${API_URL}/admin/packages/custom-package-requests/${id}`, {
    method: "DELETE",
    headers: jsonHeaders,
  });

// ─────────────────────────────────────────────
// ADMIN — Organizers (partners)
// ─────────────────────────────────────────────

// Partner users (business/individual accounts) for the organizer dropdown.
// params: { search, page, limit } — limit max 200.
// Resolves to { partners: [{ id, displayName, fullName, businessName,
// accountType, email, isVerified }], pagination }.
export const getPartners = async (params = {}) =>
  (
    await request(`${API_URL}/admin/users/partners`, {
      method: "GET",
      headers: jsonHeaders,
      params,
    })
  ).data;

// Read-only views of everything assigned to one organizer.
// params: { page, pageSize }
export const getOrganizerPackages = (organizerId, params = {}) =>
  request(`${API_URL}/admin/organizers/${organizerId}/packages`, {
    method: "GET",
    headers: jsonHeaders,
    params,
  });

export const getOrganizerQuickTrips = (organizerId, params = {}) =>
  request(`${API_URL}/admin/organizers/${organizerId}/quick-trips`, {
    method: "GET",
    headers: jsonHeaders,
    params,
  });

export const getOrganizerMetrics = (organizerId) =>
  request(`${API_URL}/admin/organizers/${organizerId}/metrics`, {
    method: "GET",
    headers: jsonHeaders,
  });

// Per-listing booking stats (any listing, deleted included). Resolves to
// { packageId | quickTripId, bookings: { total, pending, confirmed, failed,
// travellers, lastBookingAt }, revenue: [{ currency, amount, bookings }] }.
export const getPackageMetrics = (id) =>
  request(`${API_URL}/admin/packages/${id}/metrics`, {
    method: "GET",
    headers: jsonHeaders,
  });

export const getQuickTripMetrics = (id) =>
  request(`${API_URL}/admin/quick-trips/${id}/metrics`, {
    method: "GET",
    headers: jsonHeaders,
  });
